import { tr } from '@/lib/i18n-core';
import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { del, get, uploadWithProgress } from '@/lib/api';
import { describeError } from '@/lib/auth';
import { Alert, Button, Card, CardContent, CardDescription, CardHeader, CardTitle, Field, Input, Select, Textarea } from '@/components/ui/primitives';
import { useToast } from '@/components/ui/toast';

interface StoredLetterhead {
  id: string;
  branchId: string | null;
  imageKey: string | null;
  lines: string[];
  footer: string | null;
}

const KEY = ['branch-letterheads'];

export function ImageLetterheadCard({ readOnly }: { readOnly: boolean }) {
  const toast = useToast();
  const queryClient = useQueryClient();
  const [branchId, setBranchId] = useState('');
  const [file, setFile] = useState<File | null>(null);
  const [lines, setLines] = useState('');
  const [footer, setFooter] = useState('');
  const stored = useQuery({ queryKey: KEY, queryFn: () => get<StoredLetterhead[]>('/org/branding/letterheads') });
  const branches = useQuery({ queryKey: ['branches'], queryFn: () => get<{ id: string; name: string }[]>('/org/branches'), retry: false });

  const current = (stored.data ?? []).find((row) => (row.branchId ?? '') === branchId) ?? null;

  const save = useMutation({
    mutationFn: () => {
      const form = new FormData();
      if (file) form.append('file', file);
      if (branchId) form.append('branchId', branchId);
      form.append('lines', JSON.stringify(lines.split('\n').map((line) => line.trim()).filter(Boolean).slice(0, 4)));
      form.append('footer', footer.trim());
      return uploadWithProgress('/org/branding/letterhead', form, () => undefined);
    },
    onSuccess: () => {
      toast.success(tr('admin', 'letterhead_saved'));
      setFile(null);
      void queryClient.invalidateQueries({ queryKey: KEY });
    },
    onError: (error) => toast.error(tr('admin', 'could_not_save_the_letterhead'), describeError(error)),
  });
  const remove = useMutation({
    mutationFn: () => del(`/org/branding/letterhead${branchId ? `?branchId=${branchId}` : ''}`),
    onSuccess: () => {
      toast.success(tr('admin', 'letterhead_removed'));
      void queryClient.invalidateQueries({ queryKey: KEY });
    },
    onError: (error) => toast.error(tr('admin', 'could_not_remove_the_letterhead'), describeError(error)),
  });

  return (
    <Card className="lg:col-span-2">
      <CardHeader>
        <CardTitle className="text-sm">{tr('admin', 'letterhead_image')}</CardTitle>
        <CardDescription>
          {tr('admin', 'a_scanned_or_designed_banner_printed_across_the')}
        </CardDescription>
      </CardHeader>
      <CardContent className="grid gap-3 sm:grid-cols-2">
        {stored.error && <Alert variant="destructive" className="sm:col-span-2">{describeError(stored.error)}</Alert>}
        <Field label={tr('admin', 'for')}>
          <Select
            value={branchId}
            onChange={(e) => {
              setBranchId(e.target.value);
              const next = (stored.data ?? []).find((row) => (row.branchId ?? '') === e.target.value);
              setLines((next?.lines ?? []).join('\n'));
              setFooter(next?.footer ?? '');
            }}
          >
            <option value="">{tr('admin', 'the_whole_hospital')}</option>
            {(branches.data ?? []).map((branch) => (
              <option key={branch.id} value={branch.id}>
                {branch.name}
              </option>
            ))}
          </Select>
        </Field>
        <Field label={tr('admin', 'image')} hint={current?.imageKey ? tr('admin', 'an_image_is_set_choose_a_file_to_replace_it') : tr('admin', 'png_jpeg_or_webp_up_to_1_mb_at_least_three')}>
          <Input type="file" accept="image/png,image/jpeg,image/webp" disabled={readOnly} onChange={(e) => setFile(e.target.files?.[0] ?? null)} />
        </Field>
        <Field label={tr('admin', 'lines_under_the_image')} hint={tr('admin', 'up_to_four_one_per_line')}>
          <Textarea rows={3} value={lines} disabled={readOnly} onChange={(e) => setLines(e.target.value)} />
        </Field>
        <Field label={tr('admin', 'footer')} hint={tr('admin', 'up_to_300_characters')}>
          <Textarea rows={3} maxLength={300} value={footer} disabled={readOnly} onChange={(e) => setFooter(e.target.value)} />
        </Field>
        {!readOnly && (
          <div className="flex gap-2 sm:col-span-2">
            <Button onClick={() => save.mutate()} loading={save.isPending} disabled={!file && !current}>
              {tr('admin', 'save_letterhead')}
            </Button>
            {current && (
              <Button variant="ghost" onClick={() => remove.mutate()} loading={remove.isPending}>
                {tr('admin', 'remove')}
              </Button>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
